import { z } from 'zod';
import { AnimalSchema, ElementSchema, TierSchema, TriggerSchema, EffectTypeSchema, } from './enums.js';
// --- Glyph key ---
// Identifies which design-system silhouette renders for a unit. Must match
// a key in the client's GLYPHS map at packages/client/src/design-system/
// glyphs/index.ts. Drift here desyncs data → render — keep both in sync
// when adding a new glyph.
export const GlyphKeySchema = z.enum([
    'crane',
    'frog',
    'fish',
    'tiger',
    'beetle',
    'snake',
    'butterfly',
    'wolf',
    'fox',
    'pup',
    'owl',
    'mantis',
    'salamander',
    'tortoise',
    'mouse',
    'phoenix',
    'stag',
    'moth',
    'ray',
    'mite',
    'tadpole',
    'smelt',
    'koi',
    'flock',
    'bear',
    'boar',
    'wyrm',
    'caiman',
    'hawk',
    'whale',
    'serpent',
    'walking_stick',
    'pterodactyl',
    'tally_hawk',
    'skink',
    'swallow',
    'quail',
    'bee',
    'pig',
    'goldfish',
    'spider',
]);
// --- Stats ---
// Per §4.3: small numbers (SAP-style). Tier 1 base: HP 1-4, ATK 1-3 ... Tier 5: HP 6-12, ATK 6-10.
export const StatsSchema = z.object({
    hp: z.number().int().min(1).max(50),
    atk: z.number().int().min(0).max(50),
});
// --- Ability effect params (intentionally permissive — each effect type
//     defines its own param shape, validated at simulator dispatch time) ---
export const EffectParamsSchema = z.record(z.string(), z.unknown());
// --- Per-star ability spec (§4.5, §10.1) ---
// A star spec is either a single effect or a list of effects that all
// fire together when the trigger lands. Either shape carries a single
// human-readable `description` that the encyclopedia / tooltips render.
// Description max width is tuned to the shop/board tooltip's content
// area (~232px wide at 11px hand font). Past ~120 chars the text wraps
// to 4+ lines and starts pushing the board content below it. Tighten
// new ability copy if the schema rejects it — that's the signal.
const DescriptionSchema = z.string().min(1).max(120);
// Optional per-star trigger override. When set, this star spec fires on
// the named trigger INSTEAD of the ability-level trigger. Lets a single
// unit mix trigger types across its star ladder — e.g. boon_adder ★1/★2
// fire on_purchase (fresh shop buy) while ★3 fires on_refold (combine
// star-up to ★3, which a tryBuy can't reach because the shop only sells
// ★1). Dispatchers resolve `spec.trigger ?? ability.trigger` per fire.
const SingleEffectStarSpecSchema = z.object({
    trigger: TriggerSchema.optional(),
    effect: EffectTypeSchema,
    params: EffectParamsSchema,
    description: DescriptionSchema,
});
const MultiEffectStarSpecSchema = z.object({
    trigger: TriggerSchema.optional(),
    effects: z
        .array(z.object({
        effect: EffectTypeSchema,
        params: EffectParamsSchema,
    }))
        .min(1),
    description: DescriptionSchema,
});
export const AbilityStarSpecSchema = z.union([
    SingleEffectStarSpecSchema,
    MultiEffectStarSpecSchema,
]);
// --- Ability — trigger plus per-star progression ---
export const AbilitySchema = z.object({
    trigger: TriggerSchema,
    lottie_url: z.string().nullable(),
    stars: z.object({
        '1': AbilityStarSpecSchema,
        '2': AbilityStarSpecSchema,
        '3': AbilityStarSpecSchema,
    }),
});
// --- Renderer (§10.1, §14) ---
// A facet describes one polygon in the unit silhouette. Two color
// modes are supported:
//   - themed: `fill` ('primary'|'accent') + `shade` (-1..+1 multiplier
//     applied to the unit's primary/accent color). Used for the
//     standard two-tone faceted look most units want.
//   - literal: `color` (hex string like '#FF0000'). For per-facet
//     accents that don't fit the primary/accent scheme (e.g., koi
//     spots, eye highlights).
// Both branches are optional at the schema level so a JSON can ship
// partial facets during authoring; the future facet renderer will
// pick whichever mode is present and fall back sensibly when both
// are absent.
export const FacetSchema = z.object({
    id: z.string(),
    polygon: z.string(), // SVG-style coordinate string
    fill: z.enum(['primary', 'accent']).optional(),
    shade: z.number().min(-1).max(1).optional(),
    color: z.string().optional(), // literal hex for custom-tinted facets
    opacity: z.number().min(0).max(1).optional(),
});
// Drop shadow accepts either a Gaussian blur shadow (`blur`) or an
// elliptical ground shadow (`rx` + `ry`). The future facet renderer
// will branch on whichever set is present.
export const DropShadowSchema = z.object({
    offset_x: z.number(),
    offset_y: z.number(),
    blur: z.number().nonnegative().optional(),
    rx: z.number().nonnegative().optional(),
    ry: z.number().nonnegative().optional(),
    opacity: z.number().min(0).max(1),
});
// Facet renderer — the original two-tone polygon shape. `mode: 'facet'`
// is the implicit default for renderer blocks without an explicit mode.
const FacetRendererSchema = z.object({
    mode: z.literal('facet').optional(),
    primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    accent_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    paper_texture: z.string(), // references paper_textures.json
    facets: z.array(FacetSchema).min(1),
    drop_shadow: DropShadowSchema,
    view_box: z.string().optional(), // SVG viewBox override (default "0 0 120 120")
    facing: z.enum(['left', 'right']).optional(), // hint for symmetry-aware rendering
});
// Image renderer — per-star bitmap art. Used by units whose silhouette
// lives entirely in pre-rendered images (e.g. Court Stag uses the
// deer_3tier sheet extracted into stag-{1,2,3}.png). The actual asset
// lookup at render time still goes through the design-system's
// TIERED_GLYPHS map keyed on the unit's `glyph` field; the JSON paths
// here record design intent (which source files map to which tier).
const ImageStarSchema = z.object({
    image_path: z.string(),
    intrinsic_size: z.tuple([z.number().positive(), z.number().positive()]),
});
const ImageRendererSchema = z.object({
    mode: z.literal('image'),
    stars: z.object({
        '1': ImageStarSchema,
        '2': ImageStarSchema,
        '3': ImageStarSchema,
    }),
    drop_shadow: DropShadowSchema,
    view_box: z.string().optional(),
    facing: z.enum(['left', 'right']).optional(),
});
export const RendererSchema = z.union([FacetRendererSchema, ImageRendererSchema]);
// --- Unit definition (the JSON file shape) ---
export const UnitDefSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]*$/, 'Unit IDs must be snake_case'),
    name: z.string().min(1).max(15, 'Unit names cap at 15 chars (forged-frame banner ellipsis)'),
    animal: AnimalSchema,
    element: ElementSchema,
    glyph: GlyphKeySchema,
    tier: TierSchema,
    stats: StatsSchema,
    ability: AbilitySchema,
    renderer: RendererSchema.optional(),
    /** Token units (e.g. pocket_mouse_token spawned on pocket_mouse's
     *  death) are catalog members so the simulator can summon them by
     *  id, but they MUST be excluded from shop generation and from the
     *  /api/units encyclopedia feed. The two boundaries (`shop/generate`
     *  and `routes/units.ts`) filter on this flag. Token JSONs typically
     *  carry a passive `bench_modifier` ability — the simulator silently
     *  skips that effect type on passive triggers, making the token a
     *  no-op stat-stick in battle. */
    is_token: z.boolean().optional(),
    /** Battle-screen render hint: when true, the unit floats above the
     *  ground line (an extra lift on top of `body_offset_y`) and tilts
     *  ~10° further forward at strike peak so its lunge reads as an
     *  airborne dive instead of a ground charge. Defaults to false.
     *  Render-only — has no simulator consequence. */
    is_flying: z.boolean().optional(),
    /** Battle-screen render hint: positive pixels = unit appears HIGHER
     *  off the ground (i.e. translated up). Used to fix per-unit
     *  baseline alignment when a glyph's silhouette doesn't sit flush
     *  with the bottom of its TieredCritter bbox. Flying units get an
     *  additional fixed lift on top of this. Defaults to 0. */
    body_offset_y: z.number().int().optional(),
    /** Battle-screen render hint: scale multiplier on the unit body
     *  (1.0 = default, 0.8 = 80% of normal). Layout slot is unaffected
     *  — only the inner glyph + bobbing animation shrinks/grows.
     *  Tiny units (mite, tadpole, mouse) read more accurately at 0.7-
     *  0.85; oversized capstones (whale, phoenix) can use 1.1-1.2.
     *  Defaults to 1.0. */
    body_scale: z.number().positive().optional(),
});
//# sourceMappingURL=units.js.map